import React from 'react';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { PersonType } from '../../services/services';
import { ProcessCard, ProcessField, ProcessList } from '../Result/styels';
import ScoreBand, { colorForClass } from '../ScoreBand';
import {
  BandArea,
  ClassBlock,
  DetailNote,
  PointsBox,
  PointsLabel,
  PointsValue,
  VerdictBody,
  VerdictText,
} from './styles';
import {
  ICompanyAddress,
  ICompanyData,
  IRegistrationData,
  IScoreData,
  IScoreRestriction,
  KindData,
} from './types';

/**
 * Conteúdo de um item de resultado da esteira, escolhido pelo `kind` do
 * `additionalData[0]` (contrato C3/C9):
 *
 * - `score`        — pontuação, classe, régua A–F, restrições e renda;
 * - `dados_pj`     — ficha da empresa (só informativo, nunca aponta);
 * - `validacao_pj` — situação cadastral da empresa.
 *
 * Itens sem `kind` (Brick e afins) continuam com a lista de processos do
 * próprio Result.
 */

const formatCurrency = (value?: number | null): string | null => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/** "2026-10-08T14:30:00-03:00" → "08/10/2026 14:30". Outro formato passa. */
const formatDateTime = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAddress = (address: ICompanyAddress): string =>
  [
    [address.logradouro, address.numero].filter(Boolean).join(', '),
    address.complemento,
    address.bairro,
    [address.cidade, address.uf].filter(Boolean).join('/'),
    address.cep,
  ]
    .filter(Boolean)
    .join(' · ');

const useDetailsTranslation = () => {
  const { translationPage } = useAppTranslation();
  return translationPage(tr => tr.components.resultDetails);
};

/** Linha rótulo/valor; some quando não há valor (nada de "-" solto). */
const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) =>
  value === null || value === undefined || value === '' ? null : (
    <ProcessField>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </ProcessField>
  );

// ─── Score ──────────────────────────────────────────────────────────────────

const ScoreDetails: React.FC<{
  data: IScoreData;
  fallbackType: PersonType;
}> = ({ data, fallbackType }) => {
  const { tp } = useDetailsTranslation();
  const kind: PersonType =
    data.tipo === 'PJ' || data.tipo === 'PF' ? data.tipo : fallbackType;

  // Débitos e protestos: quantidade e valor total. Quantidade nula = o
  // provedor não informou, o que não é o mesmo que "nenhum".
  const describeRestriction = (restriction?: IScoreRestriction | null) => {
    const qtd = restriction?.qtd;
    if (qtd === null || qtd === undefined) return tp('notInformed');

    const count = tp('score.recordsCount', { count: qtd });
    const total = qtd > 0 ? formatCurrency(restriction?.total) : null;
    return total ? `${count} · ${total}` : count;
  };

  const checksQtd = data.restricoes?.cheques?.qtd;

  return (
    <>
      <VerdictBody>
        <ClassBlock color={colorForClass(data.classe)}>
          <small>{tp('score.classLabel')}</small>
          <strong>{data.classe ?? '-'}</strong>
        </ClassBlock>
        <VerdictText>
          <h3>{data.faixa ?? '-'}</h3>
          {data.probabilidade && <p>{data.probabilidade}</p>}
        </VerdictText>
        <PointsBox>
          <PointsValue>
            <b>{data.pontos ?? '-'}</b>
            <span>/1000</span>
          </PointsValue>
          <PointsLabel>{tp('score.pointsLabel')}</PointsLabel>
        </PointsBox>
      </VerdictBody>

      <BandArea>
        <ScoreBand
          score={data.pontos}
          cls={data.classe}
          kind={kind}
          scaleLabel={tp(
            kind === 'PJ' ? 'score.bandScalePj' : 'score.bandScalePf',
          )}
        />
      </BandArea>

      <ProcessList>
        <ProcessCard>
          <strong>{tp('score.restrictionsTitle')}</strong>
          <Field
            label={tp('score.debts')}
            value={describeRestriction(data.restricoes?.debitos)}
          />
          <Field
            label={tp('score.protests')}
            value={describeRestriction(data.restricoes?.protestos)}
          />
          <Field
            label={tp('score.checks')}
            value={
              checksQtd === null || checksQtd === undefined
                ? tp('notInformed')
                : tp('score.recordsCount', { count: checksQtd })
            }
          />
        </ProcessCard>

        <ProcessCard>
          <Field
            label={
              kind === 'PJ'
                ? tp('score.revenueEstimated')
                : tp('score.incomePresumed')
            }
            value={formatCurrency(data.renda) ?? tp('notInformed')}
          />
          <p>{tp('score.notProven')}</p>
          <Field
            label={tp('score.consultDate')}
            value={formatDateTime(data.consultadoEm)}
          />
        </ProcessCard>
      </ProcessList>

      <DetailNote>{tp('score.disclaimer')}</DetailNote>
    </>
  );
};

// ─── Dados Adicionais PJ ────────────────────────────────────────────────────

const CompanyDetails: React.FC<{ data: ICompanyData }> = ({ data }) => {
  const { tp } = useDetailsTranslation();

  const age =
    typeof data.idadeEmpresa === 'number'
      ? tp('company.companyAgeValue', { years: data.idadeEmpresa })
      : data.idadeEmpresa;

  const cnae = [data.cnae?.codigo, data.cnae?.descricao]
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' · ');

  const partners = data.socios ?? [];
  const addresses = (data.enderecos ?? []).map(formatAddress).filter(Boolean);
  const phones = (data.telefones ?? []).filter(phone => phone.numero);
  const emails = (data.emails ?? []).filter(Boolean);

  const phoneTypeLabel = (type?: string | null): string | null => {
    if (type === 'movel') return tp('company.phoneMobile');
    if (type === 'fixo') return tp('company.phoneLandline');
    return null;
  };

  return (
    <ProcessList>
      <ProcessCard>
        <strong>{tp('company.registrationSection')}</strong>
        <Field label={tp('company.corporateName')} value={data.razaoSocial} />
        <Field label={tp('company.tradeName')} value={data.nomeFantasia} />
        <Field label={tp('company.openingDate')} value={data.dataAbertura} />
        <Field label={tp('company.companyAge')} value={age} />
        <Field
          label={tp('company.registrationStatus')}
          value={data.situacaoCadastral}
        />
        <Field
          label={tp('company.registrationStatusDate')}
          value={data.dataSituacaoCadastral}
        />
        <Field label={tp('company.size')} value={data.porte} />
        <Field
          label={tp('company.legalNature')}
          value={data.naturezaJuridica}
        />
        <Field label={tp('company.cnae')} value={cnae} />
      </ProcessCard>

      {partners.length > 0 && (
        <ProcessCard>
          <strong>{tp('company.partners')}</strong>
          {partners.map((partner, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={index}>
              {[
                partner.nome ?? tp('notInformed'),
                partner.documento,
                partner.dataEntrada
                  ? tp('company.partnerSince', { date: partner.dataEntrada })
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ))}
        </ProcessCard>
      )}

      {addresses.length > 0 && (
        <ProcessCard>
          <strong>{tp('company.addresses')}</strong>
          {addresses.map((address, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={index}>{address}</p>
          ))}
        </ProcessCard>
      )}

      {(phones.length > 0 || emails.length > 0) && (
        <ProcessCard>
          {phones.length > 0 && <strong>{tp('company.phones')}</strong>}
          {phones.map((phone, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <p key={index}>
              {[phone.numero, phoneTypeLabel(phone.tipo)]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ))}
          {emails.length > 0 && <strong>{tp('company.emails')}</strong>}
          {emails.map(email => (
            <p key={email}>{email}</p>
          ))}
        </ProcessCard>
      )}
    </ProcessList>
  );
};

// ─── Validação de Dados PJ ──────────────────────────────────────────────────

const RegistrationDetails: React.FC<{ data: IRegistrationData }> = ({
  data,
}) => {
  const { tp } = useDetailsTranslation();

  return (
    <ProcessList>
      <ProcessCard>
        <Field label={tp('company.corporateName')} value={data.razaoSocial} />
        <Field
          label={tp('company.registrationStatus')}
          value={data.situacaoCadastral ?? tp('notInformed')}
        />
        <Field
          label={tp('company.registrationStatusDate')}
          value={data.dataSituacaoCadastral}
        />
      </ProcessCard>
    </ProcessList>
  );
};

// ─── Entrada ────────────────────────────────────────────────────────────────

interface ResultDetailsProps {
  data: KindData;
  /** Tipo do atendimento, para quando o Score não disser o próprio tipo. */
  fallbackType: PersonType;
}

const ResultDetails: React.FC<ResultDetailsProps> = ({
  data,
  fallbackType,
}) => {
  switch (data.kind) {
    case 'score':
      return <ScoreDetails data={data} fallbackType={fallbackType} />;
    case 'dados_pj':
      return <CompanyDetails data={data} />;
    case 'validacao_pj':
      return <RegistrationDetails data={data} />;
    default:
      return null;
  }
};

export default ResultDetails;
